import type { FC } from 'react'
import { useState } from 'react'

import type { MessageType } from '@skytech/ui'
import { MessagePopup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import DeleteIcon from 'mdi-react/DeleteIcon'
import LockOpenIcon from 'mdi-react/LockOpenIcon'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import { ECOM_ORDER_STATUS } from '@/modules/ecom-policy/constants'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import { APP_ROUTES } from '@/util/app-routes'

const EcomPolicyOrderOptions: FC = () => (
  <div>
    <ApproveButton />
    <RejectButton />
    <IgnoreButton />
    <RemoveDownPaymentsButton />
  </div>
)

const INITIAL_POPUP_STATE: { type?: MessageType; message: string; title: string } = {
  message: '',
  title: '',
}

const ApproveButton: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const { ecomPolicyOrderId } = useParams()
  const [openPopup, setOpenPopup] = useState(INITIAL_POPUP_STATE)

  if (!ecomPolicyOrderId) navigate(APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST)

  const { data: order } = useQuery(queries.order(Number(ecomPolicyOrderId)), () =>
    ecomPolicyResources.getOrder(Number(ecomPolicyOrderId))
  )

  const isOrderOpenForApproval = order?.statusId === ECOM_ORDER_STATUS.AWAITING_APPROVAL

  const postStatusMutation = useMutation(
    () => ecomPolicyResources.approveOrder(Number(ecomPolicyOrderId)),
    {
      onSuccess() {
        setOpenPopup({
          type: 'success',
          message: translate('Order has been approved.'),
          title: translate('Approve order success'),
        })
        queryClient.invalidateQueries(queries.order(Number(ecomPolicyOrderId)))
        queryClient.invalidateQueries(queries.policyOrderList(), {
          refetchType: 'all',
        })
      },
      onError() {
        setOpenPopup({
          message: translate(
            'We encountered a problem while approving the order. Please refresh and try again.'
          ),
          title: translate('Approve order error'),
        })
      },
    }
  )

  if (!isOrderOpenForApproval) return null

  return (
    <>
      <Button
        variant="primary"
        Icon={<CheckIcon />}
        isLoading={postStatusMutation.isLoading}
        onClick={() => postStatusMutation.mutate()}
      >
        <Translation id="Approve" />
      </Button>

      <MessagePopup
        type={openPopup.type}
        title={openPopup.title}
        message={openPopup.message}
        isOpen={Boolean(openPopup.message)}
        onClose={() => setOpenPopup(INITIAL_POPUP_STATE)}
      />
    </>
  )
}

const RejectButton: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const { ecomPolicyOrderId } = useParams()
  const [openPopup, setOpenPopup] = useState(INITIAL_POPUP_STATE)

  if (!ecomPolicyOrderId) navigate(APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST)

  const { data: order } = useQuery(queries.order(Number(ecomPolicyOrderId)), () =>
    ecomPolicyResources.getOrder(Number(ecomPolicyOrderId))
  )

  const isOrderOpenForApproval = order?.statusId === ECOM_ORDER_STATUS.AWAITING_APPROVAL

  const postStatusMutation = useMutation(
    () => ecomPolicyResources.rejectOrder(Number(ecomPolicyOrderId)),
    {
      onSuccess() {
        setOpenPopup({
          type: 'success',
          message: translate('Order has been rejected.'),
          title: translate('Reject order success'),
        })
        queryClient.invalidateQueries(queries.order(Number(ecomPolicyOrderId)))
        queryClient.invalidateQueries(queries.policyOrderList(), {
          refetchType: 'all',
        })
      },
      onError() {
        setOpenPopup({
          message: translate(
            'We encountered a problem while rejecting the order. Please refresh and try again.'
          ),
          title: translate('Reject order error'),
        })
      },
    }
  )

  if (!isOrderOpenForApproval) return null

  return (
    <>
      <Button
        Icon={<CloseIcon />}
        isLoading={postStatusMutation.isLoading}
        onClick={() => postStatusMutation.mutate()}
      >
        <Translation id="Reject" />
      </Button>

      <MessagePopup
        type={openPopup.type}
        title={openPopup.title}
        message={openPopup.message}
        isOpen={Boolean(openPopup.message)}
        onClose={() => setOpenPopup(INITIAL_POPUP_STATE)}
      />
    </>
  )
}

const IgnoreButton: FC = () => {
  const isAdmin = useIsAdmin()

  const translate = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { ecomPolicyOrderId } = useParams()
  const [openPopup, setOpenPopup] = useState(INITIAL_POPUP_STATE)

  if (!ecomPolicyOrderId) navigate(APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST)

  const { data: order } = useQuery(queries.order(Number(ecomPolicyOrderId)), () =>
    ecomPolicyResources.getOrder(Number(ecomPolicyOrderId))
  )

  const { policyName, ignore, statusId } = order || {}

  const ignoreOrderMutation = useMutation(
    () => ecomPolicyResources.ignoreOrder(Number(ecomPolicyOrderId)),
    {
      onSuccess() {
        setOpenPopup({
          type: 'success',
          message: translate('Ordering has been unlocked for this user.'),
          title: translate('Unlock ordering success'),
        })
        queryClient.invalidateQueries(queries.order(Number(ecomPolicyOrderId)))
        queryClient.invalidateQueries(queries.policyOrderList(), {
          refetchType: 'all',
        })
      },
      onError() {
        setOpenPopup({
          message: translate(
            'We encountered a problem while unlocking ordering for user. Please refresh and try again.'
          ),
          title: translate('Unlock ordering for user error'),
        })
      },
    }
  )

  const mightIgnoreOrder =
    isAdmin && !ignore && !!policyName && [ECOM_ORDER_STATUS.DELIVERED].includes(Number(statusId))

  if (!mightIgnoreOrder) return null

  return (
    <>
      <Button
        Icon={<LockOpenIcon />}
        isLoading={ignoreOrderMutation.isLoading}
        onClick={() => ignoreOrderMutation.mutate()}
      >
        <Translation id="Unlock ordering for user" />
      </Button>

      <MessagePopup
        type={openPopup.type}
        title={openPopup.title}
        message={openPopup.message}
        isOpen={Boolean(openPopup.message)}
        onClose={() => setOpenPopup(INITIAL_POPUP_STATE)}
      />
    </>
  )
}

const RemoveDownPaymentsButton: FC = () => {
  const isAdmin = useIsAdmin()

  const translate = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { ecomPolicyOrderId } = useParams()
  const [openPopup, setOpenPopup] = useState(INITIAL_POPUP_STATE)

  if (!ecomPolicyOrderId) navigate(APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER_LIST)

  const { data: order } = useQuery(queries.order(Number(ecomPolicyOrderId)), () =>
    ecomPolicyResources.getOrder(Number(ecomPolicyOrderId))
  )

  const { downPaymentRemaining, statusId } = order || {}

  const deleteOrderDownPaymentsMutation = useMutation(
    () => ecomPolicyResources.deleteOrderDownPayments(Number(ecomPolicyOrderId)),
    {
      onSuccess() {
        setOpenPopup({
          type: 'success',
          message: translate('Down payments have been removed.'),
          title: translate('Remove down payments success'),
        })
        queryClient.invalidateQueries(queries.order(Number(ecomPolicyOrderId)))
        queryClient.invalidateQueries(queries.policyOrderList(), {
          refetchType: 'all',
        })
      },
      onError() {
        setOpenPopup({
          message: translate(
            'We encountered a problem while removing the down payments. Please refresh and try again.'
          ),
          title: translate('Remove down payments error'),
        })
      },
    }
  )

  const mightDeleteDownPayments =
    isAdmin &&
    downPaymentRemaining &&
    downPaymentRemaining > 0 &&
    [ECOM_ORDER_STATUS.DELIVERED].includes(Number(statusId))

  if (!mightDeleteDownPayments) return null

  return (
    <>
      <Button
        Icon={<DeleteIcon />}
        disabled={deleteOrderDownPaymentsMutation.isLoading}
        onClick={() => deleteOrderDownPaymentsMutation.mutate()}
      >
        <Translation id="Remove down payments" />
      </Button>

      <MessagePopup
        type={openPopup.type}
        title={openPopup.title}
        message={openPopup.message}
        isOpen={Boolean(openPopup.message)}
        onClose={() => setOpenPopup(INITIAL_POPUP_STATE)}
      />
    </>
  )
}

export default EcomPolicyOrderOptions
